import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Check, X, ChevronDown, BarChart3, Users, Lightbulb, MessageCircle, Eye, GitBranch, PenLine, ListChecks } from "lucide-react";
import { useState } from "react";

const CAL_LINK = "https://cal.com/kalza-studio-hdho6x";

const Highlight = ({ children }) => (
  <span className="relative inline-block font-accent">
    <span className="absolute left-0 right-0 bottom-[0.08em] h-[0.8em] bg-[#BE123C] -z-0" />
    <span className="relative z-10">{children}</span>
  </span>
);

const OrganicImage = ({ src, alt, className = "" }) => (
  <div
    className={`aspect-[4/5] overflow-hidden ${className}`}
    style={{
      WebkitMaskImage: 'url(/images/mood/shape-mask.png)',
      maskImage: 'url(/images/mood/shape-mask.png)',
      WebkitMaskSize: '100% 100%',
      maskSize: '100% 100%',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
    }}
  >
    <img src={src} alt={alt || ""} className="w-full h-full object-cover" />
  </div>
);

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#1C1210]/10 last:border-0">
      <button onClick={() => setOpen(!open)} className="w-full flex items-start justify-between gap-4 py-5 text-left">
        <span className="font-serif text-lg text-[#1C1210]">{q}</span>
        <ChevronDown size={18} className={`text-[#BE123C] flex-shrink-0 mt-1 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="font-body text-sm text-[#6B6560] leading-relaxed pb-5">{a}</p>}
    </div>
  );
};

const LOGOS = [
  { src: "/images/brand-articulations.png", alt: "Brand Articulations" },
  { src: "/images/uefa.png", alt: "UEFA Champions League" },
  { src: "/images/pyszne.png", alt: "Pyszne.pl" },
  { src: "/images/coca-cola.png", alt: "Coca-Cola" },
  { src: "/images/lukkien.png", alt: "Lukkien" },
  { src: "/images/just-eat.png", alt: "Just Eat Takeaway" },
  { src: "/images/house-of-retouching.png", alt: "House of Retouching" },
  { src: "/images/samsung.png", alt: "Samsung" },
  { src: "/images/ben-jerrys.png", alt: "Ben & Jerry\'s" },
  { src: "/images/heineken.png", alt: "Heineken" },
];

const testimonials = [
  {
    id: 1,
    quote: "We were about to spend 8,000 euros on a new website when someone suggested we do this sprint first. Turned out the problem was not the website at all. That one insight alone saved us from a very expensive mistake.",
    author: "Julia Werner",
    role: "Co-founder of a wellness brand",
    initials: "JW",
  },
  {
    id: 2,
    quote: "I kept telling myself I would figure out the brand later, after the next launch, after the next client. Six months went by and nothing changed. Two hours with Ola and a week later I had everything mapped out. Should have done this a year ago.",
    author: "Tomek Nowicki",
    role: "Fitness coach and online course creator",
    initials: "TN",
  },
  {
    id: 3,
    quote: "I was so tired of tweaking my brand every few weeks and still feeling like it was wrong. This sprint gave me the clarity I needed right before making a big investment in my business. It was the best decision I could have made at that moment.",
    author: "Marta Kowalska",
    role: "Interior design studio owner",
    initials: "MK",
  },
];

export default function SprintPage() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isTestimonialAnimating, setIsTestimonialAnimating] = useState(false);

  const handleTestimonialSelect = (index) => {
    if (index === activeTestimonial || isTestimonialAnimating) return;
    setIsTestimonialAnimating(true);
    setTimeout(() => {
      setActiveTestimonial(index);
      setTimeout(() => setIsTestimonialAnimating(false), 400);
    }, 200);
  };

  const currentTestimonial = testimonials[activeTestimonial];

  return (
    <main data-testid="sprint-page">
      {/* ═══ 1. HERO ═══ */}
      <section className="pt-32 pb-20 md:pb-28 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-8 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-3">
              <span className="font-eyebrow text-white mb-6 block">YOUR BRAND DIAGNOSTIC</span>
              <h1 className="mb-6">
                <span className="font-display text-4xl md:text-5xl lg:text-6xl text-white block leading-[0.95]">BRAND REALITY CHECK</span>
                <span className="font-accent text-2xl md:text-3xl lg:text-4xl text-[#BE123C] block leading-[1.1] mt-2">the honest look you've been avoiding</span>
              </h1>
              <p className="font-body text-base md:text-lg text-white/60 leading-relaxed max-w-xl mb-8">
                A strategic diagnostic for founders who never have time to look at their own brand from the outside. I show you what strangers actually see, how you compare to competitors, and what to fix first. So you can finally stop second-guessing your brand. Full clarity in 7 days.
              </p>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="lg:hidden flex justify-center mb-10">
                <img src="/images/report-mockup.png" alt="Brand Reality Report preview" className="w-full max-w-xs" />
              </motion.div>
              <div className="flex flex-wrap gap-4 mb-10">
                {["Two hours of your time", "Full report in 7 days", "Competitor analysis included", "30-day action plan"].map((label, i) => (
                  <div key={i} className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-full px-4 py-2">
                    <Check size={12} className="text-[#D4A0A0]" />
                    <span className="font-body text-xs text-white/60 tracking-wide">{label}</span>
                  </div>
                ))}
              </div>
              <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-bold text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#BE123C] transition-colors duration-300">
                GET YOUR SPRINT <ArrowRight size={16} />
              </a>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="hidden lg:block lg:col-span-2">
              <img src="/images/report-mockup.png" alt="Brand Reality Report preview" className="w-full scale-125 origin-center" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 2. THE PROBLEM ═══ */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-6 block">THE PROBLEM</span>
              <h2 className="mb-8">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">SOMETHING DOESN'T</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">match anymore</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7] max-w-lg">
                <p>Your clients stay, refer you, send you thank-you messages. But when was the last time you opened your own website and thought "yes, this is exactly right"?</p>
                <p>You know your business is ready for more. The quality is there. The results are there. But the brand has not kept up. The right clients are out there but your brand is not reaching them yet. This sprint shows you exactly why and what to do about it.</p>
                <p>Seven days, full clarity.</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="lg:pt-16">
              <span className="font-eyebrow text-[#8B1A3A] mb-8 block">YOU MIGHT RECOGNISE THIS</span>
              <div className="space-y-5">
                {[
                  "You opened your website last week and immediately thought of three things you wanted to change",
                  "Your business has grown but the brand still looks like the version you built in the first six months",
                  "You want to raise your prices but when you imagine putting the new number on your site, your stomach tightens",
                  "You posted a reel last month that felt safe and generic and you knew it while you were editing it",
                  "Your website and Instagram look like they belong to two different businesses",
                  "The right clients are out there but your brand is not reaching them yet",
                  "At a networking event last month someone asked what you do and you gave a different answer than the one on your homepage",
                ].map((sign, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check size={16} className="text-[#BE123C] flex-shrink-0 mt-1" />
                    <span className="font-body text-base text-[#4A4540]">{sign}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ DIVIDER — Unfair Advantage ═══ */}
      <section className="relative py-14 md:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/40" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-snug">
              Welcome to your new <Highlight>unfair advantage</Highlight>.
            </p>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-snug mt-1">
              Years of experimenting compressed into one strategic shortcut.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ 3. HOW IT WORKS ═══ */}
      <section className="py-24 md:py-32 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-6 block">A STRATEGIC STOP</span>
            <h2 className="mb-4">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">HOW IT WORKS</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">in four stages</span>
            </h2>
            <p className="font-body text-base text-[#6B6560] leading-[1.7] max-w-2xl mb-6">
              I open your website the way a stranger would. No context, no backstory, just the first impression. Then I compare what your clients say about you with what your homepage says. The gap between those two is where the problem lives.
            </p>
            <p className="font-body text-base text-[#4A4540] leading-[1.7] max-w-2xl mb-16">
              Every finding is tied to a business consequence. If your pricing page sends mixed signals, I tell you what that costs you. This is a strategic diagnostic, not a design review.
            </p>
          </motion.div>

          <div className="relative">
            <div className="hidden lg:block absolute left-8 top-0 bottom-24 w-px bg-[#BE123C]/20" />
            <div className="space-y-12 lg:space-y-16">
              {[
                { num: "01", title: "Intake form", subtitle: "~30 min", body: "You fill out a questionnaire about your business: what you sell, who your clients are and where you feel stuck. This gives me the context I need before I look at your brand from the outside." },
                { num: "02", title: "Strategic 1:1 conversation", subtitle: "45 min · online", body: "We sit down and I listen. When you talk about your clients your eyes light up. When I ask about your pricing you hesitate. That mismatch between what you say and how you say it is where the real issue lives." },
                { num: "03", title: "Deep brand analysis", subtitle: "this is where the sprint happens", body: "Your website goes next to three to five competitors. I read your client reviews and pull out the words people use to describe you. Your homepage runs through a 5-second attention test to see what a stranger notices first. I check whether your Instagram bio matches your offer page. This follows a methodology built over 15 years across brands from startups to global corporations." },
                { num: "04", title: "1:1 Delivery walkthrough", subtitle: "45 min · online", body: "You receive the full Brand Reality Report. We go through every finding on screen so you see what I saw, why it matters and where to start. Most founders open their laptop that evening and start making changes." },
              ].map((step, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }} className="relative lg:pl-24">
                  <div className="hidden lg:flex absolute left-5 top-1 w-7 h-7 rounded-full bg-white border-2 border-[#BE123C]/30 items-center justify-center">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#BE123C]" />
                  </div>
                  <div className="flex items-baseline gap-4 mb-3">
                    <span className="font-accent text-3xl text-[#BE123C]/25">{step.num}</span>
                    <div>
                      <h3 className="font-serif text-xl text-[#1C1210]">{step.title}</h3>
                      {step.subtitle && <span className="font-body text-xs text-[#6B6560]">{step.subtitle}</span>}
                    </div>
                  </div>
                  <p className="font-body text-sm text-[#6B6560] leading-relaxed max-w-xl lg:ml-12">{step.body}</p>
                </motion.div>
              ))}
            </div>
            <p className="font-eyebrow text-[#8B1A3A] text-center mt-16">DELIVERED IN 7 BUSINESS DAYS</p>
          </div>
        </div>
      </section>

      {/* ═══ HOW I THINK ═══ */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/kalza-bg.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-6 block">HOW I THINK</span>
              <h3 className="mb-10 max-w-lg">
                <span className="font-display text-2xl md:text-3xl lg:text-4xl text-[#1C1210] leading-[0.95]">MOST FOUNDERS THINK THEIR PROBLEM IS THE INSTAGRAM FEED OR A LOGO. </span>
                <span className="font-accent text-2xl md:text-3xl lg:text-4xl text-[#BE123C] leading-[1.1]">It is not.</span>
              </h3>
              <div className="space-y-5 font-body text-base text-[#6B6560] leading-[1.7]">
                <p>
                  When I diagnose your brand, I analyse four sources of truth:
                </p>
                <div className="space-y-3 pl-1">
                  {[
                    "What a stranger sees in their first contact with your business",
                    "What you as the owner believe you are selling",
                    "What your paying clients actually say about you",
                    "What your competitors are signalling in the same market",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="font-accent text-lg text-[#BE123C]/40 mt-0.5 flex-shrink-0">{i + 1}.</span>
                      <span className="font-body text-base text-[#4A4540]">{item}</span>
                    </div>
                  ))}
                </div>
                <p>
                  Think of it like four pieces of a puzzle that should form one clear picture. When the pieces fit, people simply get it and it makes sense to them. When they don't - potential clients feel something is off and leave. My job is to show you what's off.
                </p>
                <p>
                  How I connect these dots is a proprietary methodology I built based on my favourite Jungian archetypes, behavioral economics, narrative psychology, semiotics, Human Design and Wealth Dynamics and overall passion over human mind. Each framework gives me a different angle on why your brand feels the way it does to people who encounter it.
                </p>
                <p>
                  By the time you read the report, the fog lifts. You gain awareness to see what's really going on in your business. You feel new source of energy, because you know exactly what to do next.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="hidden lg:block lg:pt-16">
              <img src="/images/mood/ola-process.jpg" alt="Aleksandra working on brand strategy" className="w-full shadow-lg" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 4. WHAT YOU RECEIVE ═══ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-6 block">A REPORT BUILT FOR ACTION</span>
            <h2 className="mb-8">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">WHAT YOU RECEIVE</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">your Brand Reality Check Report</span>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start mb-10">
              <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="order-2 lg:order-1 lg:pt-2">
                <div className="font-body text-base text-[#6B6560] leading-[1.7]">
                  <p className="mb-4">Your Brand Reality Check Report is built around three simple questions:</p>
                  <div className="space-y-3 pl-1 mb-4">
                    <div className="flex items-start gap-3">
                      <span className="font-accent text-lg text-[#BE123C]/40 mt-0.5 flex-shrink-0">1.</span>
                      <span className="text-[#4A4540]">Where are you now? How your brand is currently perceived by people who have never heard of you.</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-accent text-lg text-[#BE123C]/40 mt-0.5 flex-shrink-0">2.</span>
                      <span className="text-[#4A4540]">Where should you be? What your brand should look like based on what I found about your business, your clients and your market.</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-accent text-lg text-[#BE123C]/40 mt-0.5 flex-shrink-0">3.</span>
                      <span className="text-[#4A4540]">What is the plan? Concrete steps you can start implementing today with no designer or developer required for the first moves.</span>
                    </div>
                  </div>
                  <p>Includes two 1:1 sessions (45 min each): a strategic conversation before the analysis and a delivery walkthrough after. Here is what your report will contain:</p>
                </div>
              </motion.div>
              <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="order-1 lg:order-2 lg:-mt-48">
                <img src="/images/report-mockup.png" alt="Brand Reality Check Report preview" className="w-full" />
              </motion.div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: BarChart3, title: "6-Dimension Brand Diagnostic Report", body: "A scored breakdown of six brand areas. You see the exact number for each one and know which to fix first" },
                { icon: Users, title: "Competitor Analysis Report", body: "Your website next to three to five competitors. You see exactly where you blend in and where you stand out" },
                { icon: Lightbulb, title: '"What You Really Sell" Reframe Sheet', body: "The gap between what you think you sell and what people actually pay you for. One founder discovered her clients bought 'peace of mind' while her website sold 'interior design services'" },
                { icon: MessageCircle, title: "Communication Gaps Breakdown", body: "The specific sentences on your website where a potential client stops reading and leaves. I highlight each one and explain what they should say instead" },
                { icon: Eye, title: "Visual Attention Analysis", body: "Where people actually look on your website in the first 5 seconds. Eye-tracking technology verified by MIT. You see what gets noticed, what gets ignored and what to move where" },
                { icon: GitBranch, title: "Strategic Tension Map", body: "Your About page says one thing, your offer page says another, your reviews say a third. This map shows all of them at once so you know which to fix first" },
                { icon: PenLine, title: "Founder's Honest Briefing", body: "A candid written assessment where I tell you what I see, what it means for your revenue and what I would do this week if this were my business" },
                { icon: ListChecks, title: "30-Day Action Plan", body: "Five concrete steps ranked by impact that you can implement this week without hiring a designer or developer" },
              ].map((item, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }} className="py-4">
                  <item.icon size={22} className="text-[#BE123C] mb-4" />
                  <h3 className="font-serif text-base text-[#1C1210] mb-2">{item.title}</h3>
                  <p className="font-body text-sm text-[#6B6560] leading-relaxed">{item.body}</p>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-12">
              <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-bold text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#BE123C] transition-colors duration-300">
                I WANT THIS FOR MY BRAND <ArrowRight size={16} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ DIVIDER — 7 days ═══ */}
      <section className="relative py-14 md:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/40" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-snug">
              Most founders spend months guessing what's wrong with their brand. This sprint gives you the answer in <Highlight>7 days</Highlight>
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ 5. WHAT CHANGES ═══ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-6 block">THE CLARITY KICKS IN</span>
            <h2 className="mb-6">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">THIS IS WHAT CHANGES</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">after the sprint</span>
            </h2>
            <p className="font-body text-base text-[#6B6560] leading-[1.7] max-w-2xl mb-12">
              Every founder who finishes this sprint tells me the same thing: they wish they had done it sooner.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#1C1210] mb-6 block">RIGHT AWAY</span>
              <div className="space-y-5">
                {[
                  "You see a clear score for each area of your brand and know which one to strengthen first",
                  "You find the three sentences on your homepage that were making people leave before they ever contacted you",
                  "You hear what nobody in your circle would tell you about how your brand comes across",
                  "You know what to do on Monday morning without asking five people for opinions",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check size={18} className="text-[#BE123C] flex-shrink-0 mt-1" />
                    <span className="font-body text-base text-[#4A4540] leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}>
              <span className="font-eyebrow text-[#1C1210] mb-6 block">WITHIN 30 DAYS</span>
              <div className="space-y-5">
                {[
                  "You know where you are ahead of competitors and where they are beating you",
                  "You rewrite your offer because you realise your clients value something different than what you advertise",
                  "You move your strongest selling point to the first thing people see",
                  "Your website and Instagram finally say the same thing",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check size={18} className="text-[#BE123C] flex-shrink-0 mt-1" />
                    <span className="font-body text-base text-[#4A4540] leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section className="py-16 md:py-24 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="flex flex-col items-center gap-10 py-8">
              <div className="relative px-8 max-w-3xl">
                <span className="absolute -left-2 -top-6 text-7xl font-serif text-[#2C2C2A]/10 select-none pointer-events-none">"</span>
                <p className={`text-lg md:text-2xl font-light text-[#2C2C2A] text-center leading-relaxed transition-all duration-500 ease-out ${isTestimonialAnimating ? 'opacity-0 blur-sm scale-[0.98]' : 'opacity-100 blur-0 scale-100'}`}>
                  {currentTestimonial.quote}
                </p>
                <span className="absolute -right-2 -bottom-8 text-7xl font-serif text-[#2C2C2A]/10 select-none pointer-events-none">"</span>
              </div>
              <div className="flex flex-col items-center gap-6 mt-2">
                <p className={`text-xs text-[#6B6560] tracking-[0.2em] uppercase transition-all duration-500 ease-out ${isTestimonialAnimating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}>
                  {currentTestimonial.role}
                </p>
                <div className="flex items-center justify-center gap-2">
                  {testimonials.map((t, index) => {
                    const isActive = activeTestimonial === index;
                    return (
                      <button
                        key={t.id}
                        onClick={() => handleTestimonialSelect(index)}
                        className={`relative flex items-center gap-0 rounded-full cursor-pointer transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${isActive ? 'bg-[#2C2C2A] shadow-lg pr-4 pl-2 py-2' : 'bg-transparent hover:bg-[#f5f5f7] p-0.5'}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-medium transition-all duration-500 ${isActive ? 'bg-[#BE123C] text-white ring-2 ring-white/30' : 'bg-[#e5e2de] text-[#6B6560] hover:scale-105'}`}>
                          {t.initials}
                        </div>
                        <div className={`grid transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${isActive ? 'grid-cols-[1fr] opacity-100 ml-2' : 'grid-cols-[0fr] opacity-0 ml-0'}`}>
                          <div className="overflow-hidden">
                            <span className={`text-sm font-medium whitespace-nowrap block transition-colors duration-300 ${isActive ? 'text-white' : 'text-[#2C2C2A]'}`}>
                              {t.author}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ 6. WHO'S BEHIND THIS ═══ */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/kalza-bg.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <OrganicImage src="/images/ola-about.jpg" alt="Aleksandra Kałuża" className="shadow-2xl" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-4 block">MEET THE FOUNDER</span>
              <h2 className="mb-6">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">ALEKSANDRA KAŁUŻA</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">KALZA Studio</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7]">
                <p>I studied fine arts in Ireland and Poland before spending years inside international companies across Europe, leading teams of designers and copywriters and overseeing everything that reached the customer across multiple markets. I know what a billboard needs to say before it goes up. My job was to understand what the board wanted to communicate and make sure a real person on the street actually felt it.</p>
                <p>Over time I added my own layer to that foundation. A deep curiosity about psychology, emotions and why people trust one business over another when both deliver the same quality. That combination of strategic experience and understanding of the human mind became the deep branding process I use today.</p>
                <p>When I look at your brand, I am drawing on all of that at once. The trained eye, the creative instinct and years of watching what actually makes people stop and pay attention.</p>
              </div>
              <div className="flex flex-wrap items-center gap-8 mt-8">
                {LOGOS.map((logo, i) => (
                  <img key={i} src={logo.src} alt={logo.alt} className="h-6 md:h-8 w-auto object-contain invert grayscale opacity-50 hover:opacity-80 transition-all duration-300 mix-blend-multiply" />
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 7. IS THIS RIGHT FOR YOU ═══ */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-6 block">QUICK CHECK</span>
            <h2 className="mb-6">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">IS THIS SPRINT</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">right for your business?</span>
            </h2>
            <p className="font-body text-base text-[#6B6560] leading-[1.7] max-w-2xl mb-12">
              This sprint works for any founder who has an online presence and paying clients. A website, active social media, or both. You do not need a big team or a polished brand to qualify.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-[#f5f5f7] rounded-xl p-8 md:p-10">
                <div className="flex items-center gap-2 mb-6">
                  <span className="w-6 h-6 rounded-full bg-[#16a34a] flex items-center justify-center"><Check size={14} className="text-white" /></span>
                  <span className="font-eyebrow text-[#16a34a]">THIS IS FOR YOU IF</span>
                </div>
                <div className="space-y-3">
                  {[
                    "You have a running business with paying clients and you are ready to take your brand to the next level",
                    "You want an honest outside perspective on how your brand comes across",
                    "You want a clear starting point before investing in a full rebrand or new website",
                    "You can commit two hours this month to get full clarity on where you stand",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <Check size={14} className="text-[#16a34a] mt-1 flex-shrink-0" />
                      <span className="font-body text-sm text-[#4A4540]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#f5f5f7] rounded-xl p-8 md:p-10">
                <div className="flex items-center gap-2 mb-6">
                  <span className="w-6 h-6 rounded-full bg-[#BE123C]/15 flex items-center justify-center"><X size={14} className="text-[#BE123C]" /></span>
                  <span className="font-eyebrow text-[#1C1210]">THIS IS NOT FOR YOU IF</span>
                </div>
                <div className="space-y-3">
                  {[
                    "You are looking for someone to tell you everything is fine and your brand just needs a small tweak",
                    "You want a logo redesign or a quick visual fix without understanding the strategy behind it",
                    "You are not ready for honest feedback about how your brand comes across to strangers right now",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <X size={14} className="text-[#BE123C]/60 mt-1 flex-shrink-0" />
                      <span className="font-body text-sm text-[#6B6560]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ 8. INVESTMENT ═══ */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-white mb-6 block">BRAND REALITY CHECK STARTS HERE</span>
              <h2 className="mb-6">
                <span className="font-display text-3xl md:text-4xl text-white block leading-[0.95]">YOUR INVESTMENT</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">everything included</span>
              </h2>
              <p className="font-body text-base text-white/60 leading-[1.7] max-w-md mb-4">
                Most founders spend months wondering what is wrong with their brand. This sprint gives you the full picture in 7 days. Clear findings tied to real business consequences and a plan you can act on immediately.
              </p>
              <p className="font-body text-sm text-white/40 mb-10">
                Based on 15+ years of brand strategy across 100+ businesses.
              </p>
              <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-bold text-xs tracking-[2px] uppercase px-10 py-5 hover:bg-[#BE123C] transition-colors duration-300">
                GET YOUR SPRINT <ArrowRight size={16} />
              </a>
              <p className="font-body text-xs text-white/30 mt-4">Pay upfront or in three instalments. You choose on the payment page.</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="border border-white/10 rounded-xl p-8 md:p-10">
                <span className="font-eyebrow text-white/50 mb-6 block">WHAT'S INCLUDED</span>
                <div className="space-y-4">
                  {[
                    "Intake questionnaire and strategic conversation (45 min)",
                    "6-Dimension Brand Diagnostic Report",
                    "Competitor Analysis Report (3-5 competitors)",
                    '"What You Really Sell" Reframe Sheet',
                    "Communication Gaps Breakdown",
                    "Visual Attention Analysis (5-second test)",
                    "Strategic Tension Map",
                    "Founder's Honest Briefing (my personal diagnosis)",
                    "30-Day Action Plan",
                    "Delivery walkthrough call (45 min)",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check size={16} className="text-[#BE123C] flex-shrink-0 mt-0.5" />
                      <span className="font-body text-sm text-white/80">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ WHAT COMES NEXT ═══ */}
      <section className="py-20 md:py-28 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-6 block">WHAT COMES NEXT</span>
              <h2 className="mb-6">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">FROM DIAGNOSIS</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">to a full brand system</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7]">
                <p>Some founders take the report and start implementing that same evening.</p>
                <p>Others realise they want to turn the diagnosis into a full brand system. If that is you, the Brand Foundation is the natural next step. Many founders combine both so they work with one person from diagnosis to finished brand.</p>
              </div>
              <div className="mt-8">
                <Link to="/brand-foundation" className="inline-flex items-center justify-center gap-2 border border-[#1C1210]/20 text-[#1C1210] font-body font-bold text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#1C1210]/5 transition-colors duration-300">
                  EXPLORE BRAND FOUNDATION <ArrowRight size={16} />
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="space-y-4">
                <span className="font-eyebrow text-[#1C1210] mb-4 block">BRAND FOUNDATION INCLUDES</span>
                {[
                  "Six proprietary brand exercises over one month",
                  "Strategic in-depth interviews with your real customers",
                  "Brand values, character and positioning defined",
                  "Ideal customer profile built from real data",
                  "Brand voice and language extraction",
                  "Full Brand Foundation Report (your brand book)",
                  "Custom AI agent trained on your brand voice",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check size={16} className="text-[#BE123C] flex-shrink-0 mt-0.5" />
                    <span className="font-body text-sm text-[#4A4540]">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 9. FAQ ═══ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-6 block">STILL THINKING?</span>
            <h2 className="mb-10">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">QUESTIONS</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">founders ask before buying</span>
            </h2>
            <div>
              <FaqItem q="My business is small. Is this sprint still relevant for me?" a="Then it is even more relevant. Every hour you spend guessing what is wrong with your brand is an hour you could spend serving clients. Full picture in 7 days without becoming a brand expert yourself." />
              <FaqItem q="I think I already know what's wrong with my brand. Will this still help?" a="Most founders have a feeling but cannot name it precisely. One client was convinced her problem was her logo. The sprint showed her the real issue was that her pricing page contradicted her homepage. Knowing something feels off and seeing the specific sentence that breaks trust are two different things." />
              <FaqItem q="I've already tried analysing my brand on my own. How is this different?" a="If you have already looked at your brand and still feel unsure, this sprint was designed for that. I bring 15 years of pattern recognition across 100+ businesses and an outside perspective you cannot give yourself." />
              <FaqItem q="Is this just another generic brand audit?" a="No. Every sprint is built around your business, your competitors and your audience. The findings are tied to what each issue is costing you in revenue, not recommendations you could find in a blog post." />
              <FaqItem q="What if I need help implementing the recommendations?" a="The sprint is designed to stand on its own. The 30-day action plan gives you five steps ranked by impact that you can start this week. But if you want to go deeper and build a full brand system, the Brand Foundation is the natural next step." />
              <FaqItem q="What happens after I click the button?" a="You go to the payment page where you can choose to pay upfront or in three instalments. Once payment is confirmed, I send you the intake questionnaire within 24 hours and we book your first conversation." />
              <FaqItem q="What are the payment options?" a="You can pay the full amount upfront or split it into three monthly instalments. Both options are available on the payment page." />
              <FaqItem q="I still have questions before I decide. Can we talk first?" a="Of course. You can book a free 15-minute call where I will answer your questions and help you decide if this sprint is the right next step for you." />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ 10. FINAL CTA ═══ */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/sand-drawing.jpg" alt="" className="w-full h-full object-cover object-right" />
          <div className="absolute inset-0 bg-[#1C1210]/60 lg:bg-transparent" />
          <div className="hidden lg:block absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(28,18,16,0.92) 30%, rgba(28,18,16,0.5) 50%, transparent 70%)' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-md">
            <h2 className="mb-6">
              <span className="font-display text-3xl md:text-4xl text-white block leading-[0.95]">READY TO SEE</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-2">what your brand is actually saying?</span>
            </h2>
            <p className="font-body text-base text-white/70 leading-relaxed mb-10">
              You have read enough to know whether this fits. If your gut is telling you it is time, trust that. Seven days from now you will know exactly where you stand and what to do about it.
            </p>
            <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-bold text-xs tracking-[2px] uppercase px-10 py-5 hover:bg-[#BE123C] transition-colors duration-300">
              GET YOUR SPRINT <ArrowUpRight size={16} />
            </a>
            <p className="font-body text-xs text-white/30 mt-4">Secure your spot. Limited sprints per month.</p>
          </div>
        </div>
      </section>
    </main>
  );
}
